import { useEffect, useRef } from 'react';
import type { BargeInManager } from '../voice/bargeIn';
import type { TurnArbiter } from '../voice/turnArbiter';
import { remoteLog } from '../utils/remoteLog';

interface SpeechEventSender {
  (event: { type: 'SPEECH_START' } | { type: 'SPEECH_END' }): void;
}

interface UseVoiceSpeakingEffectsArgs {
  isSpeaking: boolean;
  isTTSPlaying: boolean;
  voiceInterruptEnabled: boolean;
  currentRunId: string | null;
  send: SpeechEventSender;
  getOrCreateTurnArbiter: () => Pick<TurnArbiter, 'onSpeechStart' | 'onSpeechEnd'>;
  getOrCreateBargeIn: () => Pick<BargeInManager, 'state' | 'ttsStarted' | 'speechDetected'>;
  bargeIn: Pick<BargeInManager, 'speechEnded'> | null;
}

/**
 * Wire VAD speech transitions into the App-level FSM, turn arbiter, and
 * graduated barge-in controller.
 */
export function useVoiceSpeakingEffects({
  isSpeaking,
  isTTSPlaying,
  voiceInterruptEnabled,
  currentRunId,
  send,
  getOrCreateTurnArbiter,
  getOrCreateBargeIn,
  bargeIn,
}: UseVoiceSpeakingEffectsArgs): void {
  const prevSpeakingRef = useRef(false);

  useEffect(() => {
    if (isSpeaking && !prevSpeakingRef.current) {
      send({ type: 'SPEECH_START' });
      getOrCreateTurnArbiter().onSpeechStart();

      if (isTTSPlaying) {
        if (!voiceInterruptEnabled) {
          remoteLog('tts', 'voice_interrupt_blocked', {
            reason: 'toggle_off',
            runId: currentRunId,
          });
        } else {
          const bargeInManager = getOrCreateBargeIn();
          if (bargeInManager.state === 'inactive') {
            bargeInManager.ttsStarted();
          }
          bargeInManager.speechDetected();
          remoteLog('tts', 'voice_interrupt_candidate', {
            runId: currentRunId,
            wasTTSPlaying: true,
            triggerSource: 'user-speech',
            bargeInState: bargeInManager.state,
          });
        }
      }
    } else if (!isSpeaking && prevSpeakingRef.current) {
      send({ type: 'SPEECH_END' });
      getOrCreateTurnArbiter().onSpeechEnd();
      bargeIn?.speechEnded();
    }

    prevSpeakingRef.current = isSpeaking;
  }, [
    bargeIn,
    currentRunId,
    getOrCreateBargeIn,
    getOrCreateTurnArbiter,
    isSpeaking,
    isTTSPlaying,
    send,
    voiceInterruptEnabled,
  ]);
}
